import { LocalStorage } from "../../managers/LocalStorage";
import { ScreenId } from "../../scenes/GameScene";
import { OptionsLiveData, StatisticsLiveData, TableData } from "../../types/types";
import { AppDebug, AppDebugLogType } from "../debug/AppDebug";
import { VariationId } from "../screen/TableScreen";

/**
 * Custom app-specific localstorage data and its handling
 * (error correction, default values etc)
 * @see LocalStorage
 */
export class AppLocalStorage {
    private static storagePrefix: string = "PLDBelote3in1_";

    public static GetLastPlayedVariationId(defaultValue: VariationId): VariationId {
        try {
            return LocalStorage.GetObject(this.storagePrefix+"lastVariationId", defaultValue);
        }
        catch (e: unknown) {
            LocalStorage.DeleteItem(this.storagePrefix+"lastVariationId");
            location.reload();
            return null;
        }
    }

    public static SetLastPlayedVariationId(value: number): void {
        // console.log("SetLastPlayedVariationId", value);
        LocalStorage.SetInt(this.storagePrefix+"lastVariationId", value);
    }

    public static GetLastVisitedScreenId(defaultValue: ScreenId): ScreenId {
        try {
            return LocalStorage.GetInt(this.storagePrefix+"lastScreenId", defaultValue);
        }
        catch (e: unknown) {
            LocalStorage.DeleteItem(this.storagePrefix+"lastScreenId");
            location.reload();
            return null;
        }
    }

    public static SetLastVisitedScreenId(value: number): void {
        LocalStorage.SetInt(this.storagePrefix+"lastScreenId", value);
    }

    /**
     * NOTE: now shared.
     * @param value 
     */
    public static SetOptionsSharedData(value: OptionsLiveData): void {
        LocalStorage.SetObject(this.storagePrefix+"options_shared", value);
    }

    public static GetOptionsSharedData(defaultValue: OptionsLiveData = null): OptionsLiveData | null {
        try {
            return LocalStorage.GetObject(this.storagePrefix+"options_shared", defaultValue);
        }
        catch (e: unknown) {
            LocalStorage.DeleteItem(this.storagePrefix+"options_shared");
            location.reload();
            return null;
        }
    }

    public static SetOptionsVariationData(variationId: VariationId, value: OptionsLiveData): void {
        LocalStorage.SetObject(this.storagePrefix+"options_" + variationId, value);
    }

    public static GetOptionsVariationData(variationId: VariationId, defaultValue: OptionsLiveData = null): OptionsLiveData | null {
        try {
            return LocalStorage.GetObject(this.storagePrefix+"options_" + variationId, defaultValue);
        }
        catch (e: unknown) {
            LocalStorage.DeleteItem(this.storagePrefix+"options_" + variationId);
            location.reload();
            return null;
        }
    }

    public static SetTableData(variationType: VariationId, value: TableData): void {
        if (!AppDebug.CanSaveTable()) {
            return;
        }

        AppDebug.log([
            `%c${VariationId[variationType]} / tableData:`, "color:orange;font-weight:bold;",
            `${JSON.stringify(value)}`,
        ],
            AppDebugLogType.DEBUG,
        );

        LocalStorage.SetObject(this.storagePrefix+"variation_table_" + variationType, value);
    }

    public static GetTableData(variationType: VariationId, defaultValue: TableData = null): TableData | null {
        try {
            return LocalStorage.GetObject(this.storagePrefix+"variation_table_" + variationType, defaultValue);
        }
        catch (e: unknown) {
            // console.log(e);
            LocalStorage.DeleteItem(this.storagePrefix+"variation_table_" + variationType);
            location.reload();
            return null;
        }
    }

    public static DeleteTableData(variationType: VariationId): void {
        if (!AppDebug.CanSaveTable()) {
            return;
        }

        LocalStorage.DeleteItem(this.storagePrefix+"variation_table_" + variationType);
    }

    public static SetStatisticsData(variationType: VariationId, value: any): void {
        // console.log("statistics("+variationType+")", value, JSON.stringify(value))
        LocalStorage.SetObject(this.storagePrefix+"variation_stats_" + variationType, value);
    }

    public static GetStatisticsData(variationType: VariationId, defaultValue: StatisticsLiveData = null): StatisticsLiveData | null {

        try {
            return LocalStorage.GetObject(this.storagePrefix+"variation_stats_" + variationType, defaultValue);
        }
        catch (e: unknown) {
            LocalStorage.DeleteItem(this.storagePrefix+"variation_stats_" + variationType);
            location.reload();
            return null;
        }


        
    }

    /**
     * 
     * @returns 1 for enabled, 0 for disabled
     */
    public static GetMusicEnabled(): boolean {
        const valueAsInt: number = LocalStorage.GetInt(this.storagePrefix+"musicEnabled", 1);
        return valueAsInt ? true : false;
    }

    /**
     * Set music enabled or disabled. 
     * Use 1 for enabled, 0 for disabled.
     */
    public static SetMusicEnabled(value: boolean): void {
        LocalStorage.SetInt(this.storagePrefix+"musicEnabled", value ? 1 : 0);
    }

    /**
     * 
     * @returns 1 for enabled, 0 for disabled
     */
    public static GetSoundsEnabled(): boolean {
        const valueAsInt: number = LocalStorage.GetInt(this.storagePrefix+"soundsEnabled", 1);
        return valueAsInt ? true : false;
    }

    /**
     * Set music enabled or disabled. 
     * Use 1 for enabled, 0 for disabled.
     */
    public static SetSoundsEnabled(value: boolean): void {
        LocalStorage.SetInt(this.storagePrefix+"soundsEnabled", value ? 1 : 0);
    }
}